// GenieX Villa Experience — global overlays: doorbell ring, safety alerts.
import { motion } from "framer-motion";
import { Bell, Flame, Volume2, Droplets } from "lucide-react";

export function DoorbellOverlay({ onDismiss, onUnlock }) {
  return (
    // Outer wrapper centers via flexbox (not a transform utility class) — Framer
    // Motion writes its own inline `transform` on the motion.div, which would
    // otherwise cancel out a Tailwind -translate-x-1/2 on the same element.
    <div className="fixed inset-x-0 top-20 sm:top-24 z-50 flex justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        transition={{ type: "spring", stiffness: 260, damping: 26 }}
        data-testid="doorbell-overlay"
        className="w-full max-w-[360px] rounded-3xl border border-gold/25 bg-gradient-to-b from-black/95 to-black/85 backdrop-blur-2xl p-5 shadow-[0_40px_100px_rgba(0,0,0,0.6)]"
      >
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gold/10 border border-gold/30 flex items-center justify-center">
            <motion.div animate={{ rotate: [-15, 15, -15] }} transition={{ duration: 0.6, repeat: 3 }}>
              <Bell className="w-5 h-5 text-gold" />
            </motion.div>
          </div>
          <div className="flex-1">
            <p className="text-[10px] tracking-[0.28em] uppercase text-gold">Video Doorbell</p>
            <p className="font-display text-lg text-white mt-1 tracking-tight">Someone at the door</p>
            <p className="text-[12px] text-dim mt-1">Live preview · Two-way audio ready</p>
          </div>
        </div>
        <div className="mt-4 aspect-video rounded-xl bg-gradient-to-br from-[#1a1c26] to-[#0b0c14] border border-white/5 overflow-hidden relative">
          <video
            src="/media/doorbell-live.mp4"
            poster="/media/doorbell-poster.jpg"
            autoPlay
            loop
            muted
            playsInline
            data-testid="doorbell-video"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute top-2 left-2 text-[10px] text-white/80 tracking-[0.24em] uppercase bg-black/40 px-1.5 py-0.5 rounded">LIVE · CAM 01 · MAIN DOOR</div>
          <motion.div
            className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500"
            animate={{ opacity: [1, 0.2, 1] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            onClick={onDismiss}
            data-testid="doorbell-dismiss"
            className="rounded-xl border border-white/15 py-2.5 text-[12px] tracking-[0.14em] uppercase text-mute hover:border-white/30"
          >
            Dismiss
          </button>
          <button
            onClick={onUnlock}
            data-testid="doorbell-unlock"
            className="rounded-xl bg-gold/90 hover:bg-gold text-black py-2.5 text-[12px] tracking-[0.14em] uppercase font-medium"
          >
            Unlock & Open
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export function SafetyOverlay({ kind }) {
  const map = {
    fire:  { color: "#ef4444", icon: Flame,    title: "Fire Detected", body: "Kitchen smoke sensor · Fire brigade auto-notified · Gas main shut off" },
    gas:   { color: "#f97316", icon: Volume2,  title: "Gas Leak",      body: "LPG concentration critical · Gas main auto shut off · Windows opening" },
    water: { color: "#38bdf8", icon: Droplets, title: "Water Leak",    body: "Bathroom floor sensor · Master valve closed · Push notification sent" },
  };
  const info = map[kind];
  if (!info) return null;
  const Icon = info.icon;
  return (
    <>
      <motion.div
        className="fixed inset-0 z-40 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.45, 0.15, 0.35, 0] }}
        transition={{ duration: 5, times: [0, 0.1, 0.4, 0.7, 1] }}
        style={{
          background: `radial-gradient(80% 60% at 50% 0%, ${info.color} 0%, transparent 70%)`,
          mixBlendMode: "screen",
        }}
      />
      <div className="fixed inset-x-0 top-24 z-50 flex justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -30 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          data-testid={`safety-overlay-${kind}`}
          className="w-full max-w-[400px] rounded-2xl border p-4"
          style={{
            borderColor: `${info.color}66`,
            background: `linear-gradient(180deg, ${info.color}18 0%, rgba(0,0,0,0.85) 100%)`,
            backdropFilter: "blur(24px)",
          }}
        >
          <div className="flex items-start gap-3">
            <motion.div
              className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: `${info.color}30`, color: info.color }}
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 0.8, repeat: 3 }}
            >
              <Icon className="w-5 h-5" />
            </motion.div>
            <div className="flex-1">
              <p className="text-[10px] tracking-[0.28em] uppercase" style={{ color: info.color }}>SAFETY ENGINE · ACTIVE</p>
              <p className="font-display text-lg text-white mt-1 tracking-tight">{info.title}</p>
              <p className="text-[12px] text-white/70 leading-relaxed mt-1">{info.body}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}
